import {Component, DestroyRef, inject, OnDestroy, OnInit, ChangeDetectionStrategy} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ProjectFile, ProjectFileControllerService, Task} from "../../../api";
import {ActivatedRoute} from "@angular/router";
import {TaskDetailsService} from "../../../services/tasks/task-details.service";
import {StateConfigService} from "../../../services/state/state-config.service";
import {PdfviewerComponent} from "../pdfviewer/pdfviewer.component";
import {BimViewerComponent} from "../bim-viewer/bim-viewer.component";
import {forkJoin} from "rxjs";
import {ChatService, ViewerModelRef} from "../../chatBIC/service/chat.service";
import {ViewerModelSource} from "../../../services/bim-viewer/bim-viewer.service";

@Component({
  selector: 'app-file-viewer',
  templateUrl: './file-viewer.component.html',
  styleUrls: ['./file-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PdfviewerComponent,
    BimViewerComponent
  ]
})
export class FileViewerComponent implements OnInit, OnDestroy {
  private fileControllerService = inject(ProjectFileControllerService);
  private stateService = inject(StateConfigService);
  private activatedRoute = inject(ActivatedRoute);
  private taskService = inject(TaskDetailsService);
  private chatService = inject(ChatService);
  private destroyRef = inject(DestroyRef);


  file!: ProjectFile;
  /**
   * Hauptmodell des Viewers. Bei einem Koordinationsmodell die erste verknuepfte Datei -- die
   * Koordinationsmodell-Datei selbst hat keine Geometrie.
   */
  primaryModel: ViewerModelSource | null = null;
  additionalIfcModels: ViewerModelSource[] = [];
  /**
   * Die Datei, fuer die der Viewer-Kontext bei chatBIC registriert ist.
   *
   * <p>Gemerkt, damit `ngOnDestroy` nur die eigene Registrierung aufraeumt: beim Wechsel von einem
   * Viewer direkt in den naechsten laeuft das Destroy der alten Ansicht nach der Registrierung der
   * neuen.
   */
  private registeredViewerFileId?: string;

  /**
   * Traegt Name, URL, Datei-Id und gespeicherten Tag zusammen. Die Id ist die Bruecke zur
   * Tag-Persistenz: der Viewer bekam bisher nur Name und URL.
   */
  private static toModelSource(file: ProjectFile): ViewerModelSource {
    return {
      name: file.name!,
      url: file.fragmentsLink!,
      fileId: file.id,
      tag: file.modelTag ?? undefined,
    };
  }

  ngOnInit(): void {
    const fileId = this.activatedRoute.snapshot.paramMap.get('id');
    const coordinationParam = this.activatedRoute.snapshot.queryParamMap.get('coordination');

    if (fileId === '0') {
      this.file = <ProjectFile>{};
      this.file.fileType = ProjectFile.FileTypeEnum.IFC;
      // @ts-ignore
      this.file.link = localStorage.getItem("IFCURL");
      localStorage.removeItem("IFCURL");

    } else {

      this.fileControllerService.getFile(this.stateService.getProjectId(), fileId!)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(file => {
        this.file = file;

          // Ein gespeichertes Koordinationsmodell wird ueber seine eigene Id adressiert und hier in
          // seine verknuepften Fachmodelle aufgeloest. Vorher trug die Route immer ein IFC plus
          // ?coordination=, sodass ein Koordinationsmodell im Viewer nicht direkt zu oeffnen war.
          if (file.fileType === ProjectFile.FileTypeEnum.COORDINATION) {
            this.applyModels((file.linkedFiles ?? []).filter(f => f.fragmentsLink));
            this.registerViewerContext();
            return;
          }

          const primaryModel = FileViewerComponent.toModelSource(file);
          const additionalIds = coordinationParam && file.fileType === ProjectFile.FileTypeEnum.IFC
            ? coordinationParam.split(',').map(id => id.trim()).filter(id => id.length > 0)
            : [];

          if (additionalIds.length === 0) {
            this.primaryModel = primaryModel;
            // chatBIC weiss sonst nicht, dass der Nutzer vor einem geoeffneten Modell sitzt: die Route
            // ist /projects/:id/files/:fileId, also meldet platformModule 'files' bzw. "Dateien".
            this.registerViewerContext();
            return;
          }

          // Koordinationsmodell: der Viewer startet erst, wenn alle Fachmodelle bekannt sind. Mit
          // primaryModel wird <app-bim-viewer> gerendert; kaemen die Zusatzmodelle erst nach dem
          // (ready) der Engine, laedt BimViewerComponent sie ohne Topics nach und die BCF-Topics
          // aus dem Pruefbericht fehlen (bugBIC#67).
          forkJoin(additionalIds.map(id =>
            this.fileControllerService.getFile(this.stateService.getProjectId(), id)
          ))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
              next: files => {
                this.additionalIfcModels = files
                  .filter(f => f.fileType === ProjectFile.FileTypeEnum.IFC && f.fragmentsLink)
                  .map(f => FileViewerComponent.toModelSource(f));
                this.primaryModel = primaryModel;
                this.registerViewerContext();
              },
              // Ohne die Zusatzmodelle wenigstens das Hauptmodell zeigen – wie bisher.
              error: () => {
                this.primaryModel = primaryModel;
                this.registerViewerContext();
              },
            });
        });
    }

  }

  /** Verteilt die aufgeloesten Fachmodelle eines Koordinationsmodells auf Haupt- und Zusatzmodelle. */
  private applyModels(files: ProjectFile[]): void {
    const models = files.map(f => FileViewerComponent.toModelSource(f));
    this.primaryModel = models[0] ?? null;
    this.additionalIfcModels = models.slice(1);
  }

  ngOnDestroy(): void {
    this.chatService.clearViewerContext(this.registeredViewerFileId);
  }

  /**
   * Meldet das geoeffnete Modell an chatBIC.
   *
   * <p>Nur der IFC-Zweig: der `fileId === '0'`-Pfad oben liest eine URL aus dem localStorage und
   * setzt nie `fragmentsLink` – der Viewer laedt dort ohnehin nichts (das Template bindet
   * `ifcUrl="file.fragmentsLink!"`).
   */
  private registerViewerContext(): void {
    if (!this.primaryModel) return;
    // Bei einem Koordinationsmodell traegt die Datei selbst keinen fragmentsLink -- deshalb geht das
    // Hauptmodell hier mit in die Liste und setViewerContext dedupliziert ueber die URL.
    const models: ViewerModelRef[] = [this.primaryModel, ...this.additionalIfcModels];
    this.chatService.setViewerContext(this.file, models);
    this.registeredViewerFileId = this.file.id;
  }

  createTask() {
    let task = {};
    this.taskService.setOpenedTask(task as Task);
  }

  protected readonly ProjectFile = ProjectFile;
  protected readonly FileTypeEnum = ProjectFile.FileTypeEnum;
}
